import clsx from "clsx";
import { LoadingCircle } from "../common";
import { ListCard } from "./Card/listCard";
import { FaCheck, FaPlus } from "react-icons/fa";
import { IoChevronForward } from "react-icons/io5";
import { SortOptions } from "../common/sortOptions";
import { useMapStore } from "../../stores/mapCordinations";
import { type RefObject, useEffect, useMemo } from "react";
import type { ListingCard } from "../../services/exploreListings";
import { usePaginationStore } from "../../stores/paginationStore";
import { usePropertyStore } from "../../stores/newStores/cartComparisonStore";

interface ListCardSidebarProps {
  filteredProperties: ListingCard[];
  showSortOptions: boolean;
  optionsRef: RefObject<HTMLDivElement>;
  toggleOptions: () => void;
  selectedSortOption: string;
  setSelectedSortOption: (selectedSortOption: string) => void;
  loading: boolean;
  showOfferBanner: boolean;
}

const ListCardSidebar = ({
  filteredProperties,
  showSortOptions,
  optionsRef,
  toggleOptions,
  selectedSortOption,
  setSelectedSortOption,
  loading,
  showOfferBanner,
}: ListCardSidebarProps) => {
  const {
    compareCount,
    setCompareCount,
    showAddEffect,
    setShowAddEffect,
    selectedIds,
    setFilteredPropertiesCompare,
    filteredPropertiesCompare,
    showListCard,
    setShowListCard,
  } = usePropertyStore();
  const { limitMap } = useMapStore();

  const { currentPage, setCurrentPage, pageStep, setPageStep } =
    usePaginationStore();

  const handleAdd = async () => {
    const newFilteredProperties = selectedIds.filter(
      (listingId) =>
        !filteredPropertiesCompare.some(
          (existingProperty) => existingProperty.listingId === listingId
        )
    );
    const newAdditions = newFilteredProperties.length;

    setCompareCount(newAdditions);
    setShowAddEffect(newAdditions > 0);

    setFilteredPropertiesCompare((prev) => [
      ...prev,
      ...newFilteredProperties.map((listingId) => ({
        listingId,
      })),
    ]);
  };

  useEffect(() => {
    if (showAddEffect) {
      setTimeout(() => {
        setShowAddEffect(false);
      }, 3000);
    }
  }, [showAddEffect]);

  const propertiesPerPage = Math.min(10, filteredProperties.length);
  const maxStepsPerFetch = Math.min(
    50,
    Math.ceil(filteredProperties.length / propertiesPerPage)
  );

  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);

  const propertiesToDisplay = useMemo(() => {
    const startIndex = (pageStep - 1) * propertiesPerPage;
    const endIndex = startIndex + propertiesPerPage;
    return filteredProperties.slice(startIndex, endIndex);
  }, [filteredProperties, pageStep, currentPage, propertiesPerPage]);

  const goToPreviousPage = () => {
    if (pageStep === 1 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setPageStep(1);
    } else if (pageStep > 1) {
      setPageStep(pageStep - 1);
    }
  };

  const goToNextPage = () => {
    if (pageStep === maxStepsPerFetch) {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
        setPageStep(1);
      }
    } else if (pageStep < maxStepsPerFetch) {
      setPageStep(pageStep + 1);
    }
  };

  const getPaginationRange = () => {
    const totalPageNumbers = Math.min(5, totalPages);
    const pageOffset = (currentPage - 1) * maxStepsPerFetch;

    let start = Math.max(
      pageOffset + pageStep - Math.floor(totalPageNumbers / 2),
      pageOffset + 1
    );
    let end = Math.min(
      start + totalPageNumbers - 1,
      pageOffset + maxStepsPerFetch
    );

    if (end - start + 1 < totalPageNumbers) {
      start = Math.max(end - totalPageNumbers + 1, pageOffset + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const paginationRange = getPaginationRange();

  useEffect(() => {
    setPageStep(1);
  }, [currentPage]);

  return (
    <div className="">
      <div
        className={clsx(
          "absolute z-10 rounded-[10px] shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]",
          showListCard ? "left-[460px]" : "left-5",
          showOfferBanner ? "top-[245px]" : "top-[190px]"
        )}
        onClick={() => setShowListCard((prev) => !prev)}
        onKeyDown={() => setShowListCard((prev) => !prev)}
      >
        <div className="w-[54px] h-[54px] bg-white rounded-[10px] cursor-pointer flex justify-center items-center">
          <IoChevronForward
            className={clsx(
              "text-black text-4xl transition-transform ease-in-out",
              showListCard ? "rotate-180" : "rotate-0"
            )}
          />
        </div>
      </div>
      <div
        className={clsx(
          "absolute flex flex-col z-10 text-black transition-colors left-0 bottom-0 pt-4 w-[444px]",
          showListCard ? "bg-white visible" : "bg-transparent invisible",
          showOfferBanner ? "h-[calc(100%_-_230px)]" : "h-[calc(100%_-_173px)]"
        )}
      >
        <div
          className={clsx(
            "h-full transition-transform",
            showListCard
              ? "translate-x-0 visible"
              : "-translate-x-[500px] invisible"
          )}
        >
          <div className="border-[1px] mb-2 border-[rgba(0,0,0,0.08)] rounded-lg mx-5 flex items-center justify-between pl-2">
            <div className="text-[rgba(50,48,47,0.70)] text-sm flex items-center">
              Results:
              <div className="text-z-shark flex items-center font-normal pl-1">
                {filteredProperties?.length === limitMap
                  ? `${filteredProperties?.length}+`
                  : filteredProperties?.length}
                <div className="ml-1 font-medium">properties</div>
              </div>
            </div>
            <SortOptions
              desktopMode={true}
              showOptions={showSortOptions}
              optionsRef={optionsRef}
              onToggleOptions={toggleOptions}
              selectedOption={selectedSortOption}
              setSelectedOption={setSelectedSortOption}
            />
          </div>
          <div
            className={clsx(
              "overflow-y-auto pt-2 overflow-x-hidden relative pl-2 pr-4 space-y-4 ml-3",
              selectedIds.length === 0
                ? "h-full pb-[100px]"
                : "h-[calc(100%-140px)] pb-[30px]"
            )}
          >
            {loading ? (
              <div className="flex justify-center items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <LoadingCircle />
              </div>
            ) : propertiesToDisplay.length > 0 ? (
              propertiesToDisplay.map((property) => (
                <div key={property.listingId}>
                  <ListCard property={property} />
                </div>
              ))
            ) : (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                No properties found.
              </div>
            )}
            {!loading && propertiesToDisplay.length > 0 && (
              <div className="flex justify-center items-center mt-6 border border-gray-300 rounded-md w-fit mx-auto">
                {/* biome-ignore lint/a11y/useButtonType: <explanation> */}
                <button onClick={goToPreviousPage} className="px-3 py-2">
                  Previous
                </button>
                {paginationRange.map((page) => (
                  // biome-ignore lint/a11y/useButtonType: <explanation>
                  <button
                    key={page}
                    onClick={() => {
                      const newStep =
                        page - (currentPage - 1) * maxStepsPerFetch;
                      if (newStep > 0 && newStep <= maxStepsPerFetch) {
                        setPageStep(newStep);
                      }
                    }}
                    className={`flex items-center justify-center text-gray-500 h-[47px] w-[47px] border-x border-gray-300 ${
                      page === (currentPage - 1) * maxStepsPerFetch + pageStep
                        ? "bg-[#368014] text-white font-bold"
                        : "bg-white hover:bg-gray-100"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                {/* biome-ignore lint/a11y/useButtonType: <explanation> */}
                <button
                  onClick={goToNextPage}
                  disabled={
                    filteredProperties.length < 500 && pageStep === totalPages
                  }
                  className={`px-3 py-2 ${
                    pageStep === totalPages && filteredProperties.length < 500
                      ? "text-gray-500"
                      : ""
                  }`}
                >
                  Next
                </button>
              </div>
            )}

            <div
              className={clsx(
                "fixed bottom-0 inset-x-0 bg-white h-[90px] shadow-[0_-4px_18.6px_0_rgba(0,0,0,0.25)] py-4 px-7",
                selectedIds.length === 0 && "hidden"
              )}
            >
              <button
                type="button"
                className={clsx(
                  "cursor-pointer w-full h-[62px]",
                  "text-white flex items-center justify-center space-x-1 font-semibold text-xl whitespace-nowrap",
                  "shadow-[0_6px_14px_0_rgba(0,0,0,0.25)]",
                  "transform transition-all duration-300 ease-in-out",
                  showAddEffect
                    ? "bg-[#50AA26] pointer-events-none"
                    : "bg-z-prussian-blue",
                  showAddEffect ? "w-[210px]" : "w-[100px]"
                )}
                onClick={handleAdd}
              >
                <div className="transition-all duration-300">
                  {showAddEffect ? (
                    <FaCheck className="text-lg" />
                  ) : (
                    <FaPlus className="text-lg" />
                  )}
                </div>
                <span
                  className={clsx(
                    "transition-all duration-300",
                    showAddEffect && "animate-[fadeIn_0.3s_ease-in-out]"
                  )}
                >
                  {showAddEffect
                    ? `Added ${compareCount} homes`
                    : "Add to comparison"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ListCardSidebar };
